import moment from "moment/moment";
import { jsPDF } from "jspdf";
import JSZip from "jszip";

import i18n from "@/plugins/i18n";
import {
  cleanStringValue,
  downloadFile,
  getImage,
  getLocalisedValue,
  sanitizeStringValue,
  wait,
  getQrCode,
} from "@/app/core-module/util/coreHelper";
import {
  splineRegular,
  splineMedium,
  splineSemiBold,
} from "@/app/core-module/util/spline-font";
import { balletRegular } from "@/app/core-module/util/ballet-font";
// import subscriberLogoImg from "@/app/user-module/assets/images/5m/logo.png";

import { EnvSubscriberHelper } from "@/app/env_subscriber_helper"; //sti

// Cache to store base64 strings so we don't fetch the same image (like the logo) 100 times in a bulk download
const imageBase64Cache = new Map();

// Pre-fetch an image URL as a base64 data URL before passing to jsPDF
// This prevents CORS failures, invalid stream data, and 0x0 dimension bugs in Adobe Acrobat
const fetchImageAsBase64 = async (url) => {
  if (!url || typeof url !== "string" || url.trim().length === 0) return null;
  
  if (imageBase64Cache.has(url)) {
    return imageBase64Cache.get(url);
  }

  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) return null;
    const blob = await response.blob();
    // Validate that it's actually an image and not an XML error or empty file
    if (blob.size === 0 || !blob.type.startsWith("image/")) {
      return null;
    }
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        imageBase64Cache.set(url, reader.result);
        resolve(reader.result);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (_) {
    return null;
  }
};

const generateFilename = (trainingCertificate, index) => {
  try {
    // civilid/employeeid_name_RigNo_coursename_expirydate(dd-mm-yy)
    const civilId = trainingCertificate.employeeCivilIdOrPassport?.trim() ?? "";
    let name = trainingCertificate.employeeName?.trim() ?? "";
    if (trainingCertificate.employee?.user) {
      let firstName = trainingCertificate.employee.user.firstName?.trim() ?? "";
      let lastName = trainingCertificate.employee.user.lastName?.trim() ?? "";
      let full = firstName;
      if (lastName) full += ` ${lastName}`;
      if (full) name = full;
    }
    const rigNo = trainingCertificate.employeeRigNumber?.trim() ?? "";

    const title = getLocalisedValue(trainingCertificate?.trainingTitle, {
      preferredLocale: "en",
    });
    const titleShort = title.split(" ")?.reduce((result, item) => {
      if (item?.length > 1) result += item[0];
      return result;
    }, "");

    const expiry =
      Number(trainingCertificate.expiresAt) === 0
        ? "NA"
        : trainingCertificate.expiresAt
          ? moment(Number(trainingCertificate.expiresAt)).format("DD-MM-YY")
          : "";

    const fileName =
      `${civilId}_${name}_${rigNo}_${titleShort}_${expiry}`.toUpperCase();
    return sanitizeStringValue(fileName);
  } catch (_) {
    return `certificate-${
      trainingCertificate.certificateNumber ?? index ?? "0"
    }-${Date.now()}`;
  }
};

export const generateTrainingCertificatePdf = async ({
  trainingCertificates,
  subscriberInfo,
  dataUriString = false,
  download = false,
  debug = false,
  imageCacheParam = 1,
}) => {
  try {
    if (!trainingCertificates?.length) return;
    //region pdf default variables and constants
    const pageHeight = 595;
    const pageWidth = 842;
    const normalFontSize = 12;

    const gridOneWidth = 184;
    const gridTwoWidth = pageWidth - gridOneWidth;

    const borderWidth = 12;

    const gridOneStart = borderWidth;
    const gridOneCenter = gridOneWidth / 2 + borderWidth / 2;
    const gridOneEnd = gridOneWidth;

    const gridTwoStart = gridOneWidth;
    const gridTwoCenter = gridTwoWidth / 2 + gridOneWidth - borderWidth / 2;
    const gridTwoEnd = pageWidth - borderWidth;

    const contentStart = gridTwoStart + 40;
    const contentEnd = gridTwoEnd - 50;
    const maxContentWidth = contentEnd - contentStart;
    const contentCenter = maxContentWidth / 2 + contentStart;

    const secondaryContentStart = contentStart - 20;
    const secondaryContentEnd = contentEnd + 20;
    const secondaryMaxContentWidth =
      secondaryContentEnd - secondaryContentStart;
    const secondaryContentCenter =
      secondaryMaxContentWidth / 2 + secondaryContentStart;

    let lineHeight = Math.floor(normalFontSize / 2);
    let currentY = lineHeight;

    const primaryColor = [107, 133, 166];
    const secondaryColor = [25, 26, 24];
    const leftGridColor = [208, 209, 203];
    const accentColor = [178, 69, 66];
    //endregion

    //region jspdf doc init
    const doc = new jsPDF({
      orientation: "l",
      unit: "px",
      format: [pageWidth, pageHeight],
      compress: true,
      putOnlyUsedFonts: true,
    });

    //region font setup
    doc.addFileToVFS("SplineSans-Regular-normal.ttf", splineRegular);
    doc.addFont(
      "SplineSans-Regular-normal.ttf",
      "SplineSans-Regular",
      "normal"
    );

    doc.addFileToVFS("SplineSans-Medium-normal.ttf", splineMedium);
    doc.addFont("SplineSans-Medium-normal.ttf", "SplineSans-Medium", "normal");

    doc.addFileToVFS("SplineSans-SemiBold-normal.ttf", splineSemiBold);
    doc.addFont(
      "SplineSans-SemiBold-normal.ttf",
      "SplineSans-SemiBold",
      "normal"
    );

    doc.addFileToVFS("Ballet_24pt-Regular-normal.ttf", balletRegular);
    doc.addFont(
      "Ballet_24pt-Regular-normal.ttf",
      "Ballet_24pt-Regular",
      "normal"
    );
    //endregion
    //endregion

    //region utility functions
    const fontType = (type, weight) => {
      if (type === "medium" || weight === 500) {
        doc.setFont("SplineSans-Medium", "normal");
      } else if (type === "semi-bold" || weight === 600) {
        doc.setFont("SplineSans-SemiBold", "normal");
      } else {
        //type=regular, weight=400
        doc.setFont("SplineSans-Regular", "normal");
      }
    };

    const fontSize = size => {
      doc.setFontSize(size * 1.2); //1pt = 1.2px //multiplication done to match the design
      lineHeight = Math.floor(size / 2);
    };

    const fontColor = (r, g, b, a) => {
      doc.setTextColor(r, g, b, a);
    };

    const nextLine = numberOfLines => {
      currentY += numberOfLines * lineHeight + 1;
    };

    const trimText = (value, maxLength, maxLines) => {
      return doc.splitTextToSize(value, maxLength).slice(0, maxLines);
    };

    const divider = yAxis => {
      doc.setLineWidth(1);
      doc.setDrawColor(...secondaryColor);
      doc.line(contentStart, yAxis ?? currentY, contentEnd, yAxis ?? currentY);
    };

    const getImageInfo = (image, width, height, { fit } = {}) => {
      height = height ?? width;
      width = width ?? height;
      let imageInfo, imageHeight, imageWidth, imageAspectRatio;

      if (fit != null && image != null) {
        try {
          imageInfo = doc.getImageProperties(image);
        } catch (_) {}

        imageHeight = imageInfo?.height ?? height;
        imageWidth = imageInfo?.width ?? width;
        imageAspectRatio = imageHeight / imageWidth;

        if (fit === "WIDTH" && width != null) {
          height = width * imageAspectRatio;
        } else if (fit === "HEIGHT" && height != null) {
          width = height / imageAspectRatio;
        }
      }

      return {
        width,
        height,
        widthOg: imageWidth,
        heightOg: imageHeight,
        aspectRatio: imageAspectRatio,
        fileType: imageInfo?.fileType?.toLowerCase() ?? "jpeg",
      };
    };

    const addImage = (image, x, y, width, height, { fit } = {}) => {
      const imageInfo = getImageInfo(image, width, height, { fit });
      const validWidth = Number(imageInfo.width);
      const validHeight = Number(imageInfo.height);

      if (
        image != null &&
        !isNaN(validWidth) &&
        !isNaN(validHeight) &&
        validWidth > 0 &&
        validHeight > 0
      ) {
        try {
          doc.addImage(
            image,
            imageInfo.fileType,
            x,
            y,
            validWidth,
            validHeight
          );
        } catch (_) {}
      }

      return { x, y, ...imageInfo };
    };
    //endregion

    //region subscriber logo
    const subscriberLogoSize = 100;
    const subscriberLogoUrl = getImage(subscriberInfo?.user?.avatar, {
      params: `?r=${imageCacheParam}`,
    });
    const subscriberLogo = await fetchImageAsBase64(subscriberLogoUrl);
    //  const subscriberLogo = subscriberLogoImg;
    //endregion

    //region subscriber other info
    const { subscriberContact, subscriberApprovalRef } =
      EnvSubscriberHelper.getEnvCertificateRelatedValues();
    //endregion

    const addPageContent = async trainingCertificate => {
      //region process data from trainingCertificate
      const domainUrl = `${process.env.VUE_APP_EMPLOYEE_URL}/${
        i18n.locale || "en"
      }`;
      const shareUrl = encodeURI(`${domainUrl}/public-profile/${trainingCertificate.employee?.user?._id}`);
      const employeeQrCodeSize = 60;
      const employeeQrCode = await getQrCode(shareUrl, employeeQrCodeSize);
      let employeeName = trainingCertificate.employeeName ?? "";
      if (trainingCertificate.employee?.user) {
        let firstName = trainingCertificate.employee.user.firstName?.trim() ?? "";
        let lastName = trainingCertificate.employee.user.lastName?.trim() ?? "";
        let full = firstName;
        if (lastName) full += ` ${lastName}`;
        if (full) employeeName = full;
      }

      const employeeCompanyName = getLocalisedValue(
        trainingCertificate?.organizationName,
        { preferredLocale: "en" }
      );

      const employeeAvatarSize = 48;
      const employeeAvatarUrl = getImage(trainingCertificate.employee?.user?.avatar, {
        params: `?r=${imageCacheParam}`,
      });
      const employeeAvatar = await fetchImageAsBase64(employeeAvatarUrl) ?? "";

      const employeeDesignation = (
        trainingCertificate.employeeDesignation ?? ""
      ).trim();

      //TODO: employeeUID - extraction
      const employeeUID = (
        trainingCertificate.employeeCivilIdOrPassport ??
        trainingCertificate.employeeNo ??
        ""
      ).trim();

      const trainingTitle = getLocalisedValue(
        trainingCertificate?.trainingTitle,
        {
          preferredLocale: "en",
        }
      );

      const trainingDescription = getLocalisedValue(
        trainingCertificate?.trainingDescription,
        {
          preferredLocale: "en",
        }
      );

      const trainingDuration = trainingCertificate.trainingDuration ?? 1;
      const trainingCompletedAt = trainingCertificate.completedAt
        ? moment(parseInt(trainingCertificate.completedAt))
        : null;
      let trainingDate = "";

      if (trainingDuration > 1 && trainingCompletedAt) {
        const trainingStartedAt = trainingCompletedAt
          .clone()
          .subtract({ days: trainingDuration - 1 });

        if (
          trainingStartedAt.year() === trainingCompletedAt.year() &&
          trainingStartedAt.month() === trainingCompletedAt.month()
        ) {
          trainingDate = `${trainingStartedAt.format(
            "DD"
          )} - ${trainingCompletedAt.format("DD")} ${trainingCompletedAt.format(
            "MMM YYYY"
          )}`;
        } else {
          trainingDate = `${trainingStartedAt.format(
            "DD MMM YYYY"
          )} - ${trainingCompletedAt.format("DD MMM YYYY")}`;
        }
      } else if (trainingCompletedAt) {
        trainingDate = trainingCompletedAt.format("DD MMM YYYY");
      }

      let trainingExpiryDate = "";

      if (Number(trainingCertificate.expiresAt) === 0) {
        trainingExpiryDate = "N/A";
      } else if (trainingCertificate.expiresAt) {
        trainingExpiryDate = moment(
          Number(trainingCertificate.expiresAt)
        ).format("DD MMM YYYY");
      }

      const certificateNumber = trainingCertificate.certificateNumber ?? "";

      const {
        trainerName,
        trainerSignature: trainerSignatureUrl,
        managingDirectorName,
        mdSignature: mdSignatureUrl,
      } =
        EnvSubscriberHelper.getEnvCertificateRelatedValues({ trainingCertificate, imageCacheParam });

      const mdSignature = await fetchImageAsBase64(mdSignatureUrl);
      const trainerSignature = await fetchImageAsBase64(trainerSignatureUrl);
      //endregion

      const data = {
        employeeQrCode,
        scanMe: "Scan me to view more",
        certificateHeading: "Certificate of Completion",
        employeeAvatar,
        thisIsToCertify: "This is to certify that",
        employeeName: cleanStringValue(employeeName),
        employeeDesignation: cleanStringValue(employeeDesignation),
        employeeCompanyName: cleanStringValue(employeeCompanyName),
        employeeUID: cleanStringValue(employeeUID),
        inRecognition:
          "“In recognition of having successfully completed the training course titled”:",
        trainingTitle: cleanStringValue(trainingTitle).toUpperCase(),
        trainingDescription: cleanStringValue(trainingDescription),
        trainingDateTitle: `Course Date: `,
        trainingDate,
        trainingExpiryDateTitle: `Valid Until: `,
        trainingExpiryDate,
        certificateNumberTitle: `Certificate No: `,
        certificateNumber,
        managingDirectorTitle: "Managing Director",
        trainerTitle: "Instructor",
        mdSignature,
        trainerSignature,
        managingDirectorName,
        trainerName: cleanStringValue(trainerName),
      };

      //region left section background color
      /*doc.setFillColor(...leftGridColor);
      doc.rect(0, 0, gridOneWidth, pageHeight, "F");*/
      doc.setDrawColor(...accentColor);
      doc.setLineWidth(0.5);
      doc.line(gridOneWidth, 0, gridOneWidth, pageHeight);
      //endregion

      //region background image
      /*const { certificateBackground } =
        EnvSubscriberHelper.getEnvCertificateRelatedValues();

      if (certificateBackground) {
        addImage(certificateBackground, 0, 0, pageWidth, pageHeight);
      }*/
      //endregion

      //region left section
      //region logo
      let subscriberLogoInfo;
      if (subscriberLogo) {
        subscriberLogoInfo = addImage(
          subscriberLogo,
          gridOneCenter - subscriberLogoSize / 2,
          60,
          subscriberLogoSize,
          subscriberLogoSize,
          { fit: "WIDTH" }
        );
      }

      /*doc.setDrawColor(40, 17, 102);
      doc.setLineWidth(5);
      doc.roundedRect(
        gridOneStart + 38 - 1,
        35 - 1,
        100 + 2,
        100 + 2,
        10,
        10,
        "S"
      );*/
      //endregion

      //region partner organization logos
      const { orgLogos } = EnvSubscriberHelper.getEnvCertificateRelatedValues();
      if (orgLogos.length) {
        //184-12=172
        //172-100=72
        //72/3=24
        //24-50-24-50-24
        const orgLogoSize = 50;
        const orgLogoPadding = 24;
        const orgLogoXAxisLeft = gridOneStart + orgLogoPadding;
        const orgLogoXAxisRight =
          gridOneStart + orgLogoSize + orgLogoPadding * 2;
        let orgLogoYAxis = 60 + (subscriberLogoInfo?.height ?? 100) * 2;
        let lastRowHeight = 0;

        orgLogos.forEach((orgLogo, index) => {
          const isOdd = index % 2;
          const { fileType, width, height } = getImageInfo(
            orgLogo,
            orgLogoSize,
            orgLogoSize,
            { fit: "WIDTH" }
          );

          doc.addImage(
            orgLogo,
            fileType,
            isOdd ? orgLogoXAxisRight : orgLogoXAxisLeft,
            orgLogoYAxis - height / 2,
            width,
            height
          );

          if (isOdd) {
            orgLogoYAxis =
              orgLogoYAxis -
              height / 2 +
              Math.max(lastRowHeight, height) +
              orgLogoPadding * 1.5;
          } else {
            lastRowHeight = height;
          }
        });
      }
      //endregion
      //endregion

      //region right section
      const certificateHeadingYaxis = 85;

      //region employee image
      if (data.employeeAvatar?.length) {
        addImage(
          data.employeeAvatar,
          contentStart,
          certificateHeadingYaxis / 1.7,
          employeeAvatarSize,
          employeeAvatarSize
        );
      }
      //endregion

      //region main heading
      fontColor(...primaryColor);
      doc.setFont("Ballet_24pt-Regular", "normal");
      fontSize(48);

      doc.text(
        data.certificateHeading,
        contentCenter,
        certificateHeadingYaxis,
        { align: "center" }
      );
      //endregion

      //region employee qrcode
      addImage(
        data.employeeQrCode,
        contentEnd - employeeQrCodeSize + 6,
        certificateHeadingYaxis / 1.9,
        employeeQrCodeSize,
        employeeQrCodeSize
      );
      //endregion

      //region set current y axis
      fontSize(17);
      nextLine(17.5);
      //endregion

      //region thisIsToCertify
      fontColor(...primaryColor);
      fontType("regular");
      fontSize(17);

      doc.text(data.thisIsToCertify, contentCenter, currentY, {
        align: "center",
      });

      //move y axis
      fontSize(17);
      nextLine(3.5);
      //endregion

      //region employee name & designation max 60 characters good
      fontColor(...secondaryColor);
      fontType("regular");

      let employeeNameFontSize = 20;
      let employeeNameFontSizeMin = 14;
      let employeeDesignationFontSize = 14;

      let employeeDesignationWidth;

      const splitEmployeeName = () => {
        fontSize(employeeDesignationFontSize);
        employeeDesignationWidth = doc.getTextWidth(data.employeeDesignation);

        const employeeNameMaxWidth =
          maxContentWidth - employeeDesignationWidth - 10;

        fontSize(employeeNameFontSize);
        const split = trimText(
          data.employeeName,
          employeeNameMaxWidth,
          employeeNameFontSize === employeeNameFontSizeMin ? 1 : undefined
        );

        if (split.length > 1) {
          employeeNameFontSize--;
          employeeDesignationFontSize--;

          return splitEmployeeName();
        }

        // console.log("employeeNameFontSize:", employeeNameFontSize);
        // console.log(
        //   "employeeDesignationFontSize:",
        //   employeeDesignationFontSize
        // );
        // console.log("split:", split.join("").length);
        return split;
      };

      const employeeNameSplit = splitEmployeeName();

      fontType("semi-bold");
      fontSize(employeeNameFontSize);
      doc.text(employeeNameSplit, contentStart, currentY);

      fontType("regular");
      fontSize(employeeDesignationFontSize);
      doc.text(
        data.employeeDesignation,
        contentEnd - employeeDesignationWidth,
        currentY
      );

      //move y axis
      fontSize(16);
      nextLine(3.5);
      //endregion

      //region employee organization name & employee id  max 80 characters good
      fontColor(...secondaryColor);
      fontType("regular");

      let employeeCompanyNameFontSize = 16;
      let employeeCompanyNameFontSizeMin = 10;

      let employeeUIDWidth;

      const splitEmployeeCompanyName = () => {
        fontSize(employeeCompanyNameFontSize);

        employeeUIDWidth = doc.getTextWidth(data.employeeUID);
        const employeeCompanyNameMaxWidth =
          maxContentWidth - employeeUIDWidth - 10;

        const split = trimText(
          data.employeeCompanyName,
          employeeCompanyNameMaxWidth,
          employeeCompanyNameFontSize === employeeCompanyNameFontSizeMin
            ? 1
            : undefined
        );

        if (split.length > 1) {
          employeeCompanyNameFontSize--;
          return splitEmployeeCompanyName();
        }

        // console.log(
        //   "employeeCompanyNameFontSize:",
        //   employeeCompanyNameFontSize
        // );
        // console.log("split:", split.join("").length);
        return split;
      };

      const employeeCompanyNameSplit = splitEmployeeCompanyName();

      doc.text(employeeCompanyNameSplit, contentStart, currentY);

      doc.text(data.employeeUID, contentEnd - employeeUIDWidth, currentY);

      //move y axis
      fontSize(16);
      nextLine(0.75);
      divider();
      nextLine(3.5);
      //endregion

      //region inRecognition
      fontColor(...primaryColor);
      fontSize(17);
      doc.text(data.inRecognition, contentCenter, currentY, {
        align: "center",
      });

      //move y axis
      fontSize(17);
      nextLine(4.5);
      //endregion

      //region training title max 170 characters good
      fontColor(...secondaryColor);
      fontType("medium");

      let trainingTitleFontSize = 24;
      let trainingTitleFontSizeMin = 23;
      let trainingTitleLines = 2;

      const splitTrainingTitle = () => {
        fontSize(trainingTitleFontSize);
        const split = trimText(
          data.trainingTitle,
          secondaryMaxContentWidth,
          trainingTitleFontSize === trainingTitleFontSizeMin
            ? trainingTitleLines
            : undefined
        );

        if (split.length > trainingTitleLines) {
          trainingTitleFontSize--;
          trainingTitleLines++;
          return splitTrainingTitle();
        }

        // console.log("trainingTitleFontSize:", trainingTitleFontSize);
        // console.log("trainingTitleLines:", trainingTitleLines);
        // console.log("split:", split.join("").length);
        return split;
      };

      const trainingTitleSplit = splitTrainingTitle();
      nextLine((trainingTitleLines - trainingTitleSplit.length) * 0.3);
      doc.text(trainingTitleSplit, contentCenter, currentY, {
        align: "center",
      });

      //move y axis
      fontSize(24);
      nextLine(1.5 * trainingTitleSplit.length);
      nextLine((trainingTitleLines - trainingTitleSplit.length) * 0.3);
      divider();
      nextLine(1.5);
      nextLine((trainingTitleLines - trainingTitleSplit.length) * 0.3);
      //endregion

      //region training description max 1000 characters good
      fontType("regular");

      if (data.trainingDescription?.length) {
        let trainingDescriptionFontSize = 12;
        let trainingDescriptionFontSizeMin = 8;
        let trainingDescriptionLines = 4;

        const splitTrainingDescription = () => {
          fontSize(trainingDescriptionFontSize);
          const split = trimText(
            data.trainingDescription,
            secondaryMaxContentWidth,
            trainingDescriptionFontSize === trainingDescriptionFontSizeMin
              ? trainingDescriptionLines
              : undefined
          );

          if (split.length > trainingDescriptionLines) {
            trainingDescriptionFontSize--;
            trainingDescriptionLines++;
            return splitTrainingDescription();
          }

          // console.log(
          //   "trainingDescriptionFontSize:",
          //   trainingDescriptionFontSize
          // );
          // console.log("trainingDescriptionLines:", trainingDescriptionLines);
          // console.log("split:", split.join("").length);
          return split;
        };

        const trainingDescriptionSplit = splitTrainingDescription();
        nextLine(
          (trainingDescriptionLines - trainingDescriptionSplit.length) * 0.3
        );
        doc.text(trainingDescriptionSplit, secondaryContentCenter, currentY, {
          align: "center",
        });
      }
      //endregion

      //region training date, certificate expiry date, certificate number
      fontSize(12);

      const trainingDateTitleYaxis = pageHeight - 155;
      const trainingDateTitleWidth = doc.getTextWidth(data.trainingDateTitle);
      const trainingExpiryDateTitleWidth = doc.getTextWidth(
        data.trainingExpiryDateTitle
      );
      const certificateNumberTitleWidth = doc.getTextWidth(
        data.certificateNumberTitle
      );
      const certificateNumberWidth = doc.getTextWidth(data.certificateNumber);

      //region titles
      fontColor(...primaryColor);
      doc.text(data.trainingDateTitle, contentStart, trainingDateTitleYaxis);
      doc.text(
        data.trainingExpiryDateTitle,
        contentStart + maxContentWidth / 2 - trainingExpiryDateTitleWidth,
        trainingDateTitleYaxis
      );
      doc.text(
        data.certificateNumberTitle,
        contentEnd - certificateNumberTitleWidth - certificateNumberWidth,
        trainingDateTitleYaxis
      );
      //endregion

      //region values
      fontColor(...secondaryColor);
      doc.text(
        data.trainingDate,
        contentStart + trainingDateTitleWidth,
        trainingDateTitleYaxis
      );
      doc.text(
        data.trainingExpiryDate,
        contentStart + maxContentWidth / 2,
        trainingDateTitleYaxis
      );
      doc.text(
        data.certificateNumber,
        contentEnd - certificateNumberWidth,
        trainingDateTitleYaxis
      );
      //endregion

      divider(trainingDateTitleYaxis + 5);
      //endregion

      //region md name, instructor name
      //region titles
      fontColor(...primaryColor);
      fontType("medium");

      const managingDirectorTitleLineWidthMax = 200;
      const managingDirectorTitleYaxis = pageHeight - 104;

      doc.text(
        data.managingDirectorTitle,
        contentStart + managingDirectorTitleLineWidthMax / 2,
        managingDirectorTitleYaxis,
        { align: "center" }
      );
      doc.text(
        data.trainerTitle,
        contentEnd - managingDirectorTitleLineWidthMax / 2,
        managingDirectorTitleYaxis,
        { align: "center" }
      );
      //endregion

      //region signatures
      // const signatureYAxis = pageHeight - 96;
      const signatureYAxis = pageHeight - 75;

      if (data.mdSignature?.length) {
        const { width, height } = getImageInfo(data.mdSignature, 100, 40, {
          fit: "HEIGHT",
        });

        addImage(
          data.mdSignature,
          contentStart + managingDirectorTitleLineWidthMax / 2 - width / 2,
          signatureYAxis,
          width,
          height
        );
      }

      if (data.trainerSignature?.length) {
        const { width, height } = getImageInfo(data.trainerSignature, 100, 40, {
          fit: "HEIGHT",
        });

        addImage(
          data.trainerSignature,
          contentEnd - managingDirectorTitleLineWidthMax / 2 - width / 2,
          signatureYAxis,
          width,
          height
        );
      }
      //endregion

      //region divider
      /*const managingDirectorTitleLineYaxis = pageHeight - 62;

      doc.line(
        contentStart,
        managingDirectorTitleLineYaxis,
        contentStart + managingDirectorTitleLineWidthMax,
        managingDirectorTitleLineYaxis
      );
      doc.line(
        contentEnd - managingDirectorTitleLineWidthMax,
        managingDirectorTitleLineYaxis,
        contentEnd,
        managingDirectorTitleLineYaxis
      );*/
      //endregion

      //region names
      fontColor(...secondaryColor);
      fontType("regular");

      // const managingDirectorNameYaxis = pageHeight - 48;
      const managingDirectorNameYaxis = pageHeight - 90;

      doc.text(
        trimText(
          data.managingDirectorName,
          managingDirectorTitleLineWidthMax,
          2
        ),
        contentStart + managingDirectorTitleLineWidthMax / 2,
        managingDirectorNameYaxis,
        { align: "center" }
      );
      doc.text(
        trimText(data.trainerName, managingDirectorTitleLineWidthMax, 2),
        contentEnd - managingDirectorTitleLineWidthMax / 2,
        managingDirectorNameYaxis,
        { align: "center" }
      );
      //endregion
      //endregion

      //region subscriber seal
      const { certificateSeal } =
        EnvSubscriberHelper.getEnvCertificateRelatedValues();
      if (certificateSeal) {
        addImage(
          certificateSeal,
          contentCenter - 50,
          pageHeight - 130,
          100,
          100
        );
      }
      //endregion

      //region footer
      fontColor(...primaryColor);
      fontSize(11);

      doc.text(
        subscriberContact,
        contentCenter,
        pageHeight - borderWidth - borderWidth / 2,
        { align: "center" }
      );
      //endregion

      //region right end
      const subscriberApprovalRefWidth = doc.getTextWidth(
        subscriberApprovalRef
      );

      doc.text(
        subscriberApprovalRef,
        gridTwoEnd - borderWidth / 2,
        pageHeight / 2 + subscriberApprovalRefWidth / 2,
        { angle: 90 }
      );
      //endregion
      //endregion
    };

    for (let index = 0; index < trainingCertificates.length; index ++) {
      const trainingCertificate = trainingCertificates[index];
      await addPageContent(trainingCertificate);

      if (trainingCertificates.length > index + 1) {
        doc.addPage();
        lineHeight = Math.floor(normalFontSize / 2);
        currentY = lineHeight;
      }
    }

    if (debug) {
      window.open(doc.output("bloburl"), "_blank");
      return true;
    } else {
      if (download) {
        if (trainingCertificates.length === 1) {
          return doc.save(`${generateFilename(trainingCertificates[0])}.pdf`);
        } else {
          return doc.save(`certificates-${Date.now()}`);
        }
      } else if (dataUriString) {
        return doc.output("datauristring");
      } else {
        return doc.output("blob");
      }
    }
  } catch (e) {
    console.log(
      "certificateHelper.generateTrainingCertificatePdf.exception:",
      e.message
    );

    return false;
  }
};

export const bulkDownloadTrainingCertificates = async ({
  trainingCertificates,
  subscriberInfo,
  zip = true,
  debug = false,
  onProgress = null,
}) => {
  try {
    if (!trainingCertificates?.length) return;

    await wait(100);

    if (zip && trainingCertificates.length > 1) {
      const zip = new JSZip();
      const certificates = zip.folder(`certificates-${Date.now()}`);
      const imageCacheParam = Math.random();

      const chunkSize = 10;
      for (let i = 0; i < trainingCertificates.length; i += chunkSize) {
        const chunk = trainingCertificates.slice(i, i + chunkSize);
        
        for (let chunkIndex = 0; chunkIndex < chunk.length; chunkIndex++) {
          const trainingCertificate = chunk[chunkIndex];
          const index = i + chunkIndex;
          let blobData;
          let ext = "pdf";
          if (trainingCertificate.certificateUrl && trainingCertificate.trainingMode === "THIRDPARTY") {
            try {
              const fileUrl = getImage(trainingCertificate.certificateUrl);
              const response = await fetch(fileUrl);
              blobData = await response.blob();
              const parts = trainingCertificate.certificateUrl.split(".");
              if (parts.length > 1) {
                const urlExt = parts.pop().toLowerCase();
                if (["pdf", "jpg", "jpeg", "png"].includes(urlExt)) {
                  ext = urlExt;
                }
              }
            } catch (e) {
              console.error("Error fetching third-party certificate:", e);
            }
          } else {
            blobData = await generateTrainingCertificatePdf({
              trainingCertificates: [trainingCertificate],
              subscriberInfo,
              imageCacheParam,
            });
          }

          if (blobData) {
            certificates.file(
              `${generateFilename(trainingCertificate, index)}.${ext}`,
              blobData
            );
          }

          if (onProgress) {
            onProgress(i + chunkIndex + 1, trainingCertificates.length);
          }

          await wait(50);
        }
      }

      if (certificates?.files && Object.keys(certificates?.files).length) {
        return await zip
          .generateAsync({ type: "blob" })
          .then(data => {
            downloadFile({ data, fileName: `certificates-${Date.now()}.zip` });
            return true;
          })
          .catch(e => {
            console.log(
              "certificateHelper.bulkDownloadTrainingCertificates:zip.generateAsync:error:",
              e.message
            );
            return false;
          });
      }
    } else {
      if (trainingCertificates.length === 1 && trainingCertificates[0].certificateUrl) {
        const certificate = trainingCertificates[0];
        const fileUrl = getImage(certificate.certificateUrl);
        const parts = certificate.certificateUrl.split(".");
        let ext = "pdf";
        if (parts.length > 1) {
          const urlExt = parts.pop().toLowerCase();
          if (["pdf", "jpg", "jpeg", "png"].includes(urlExt)) {
            ext = urlExt;
          }
        }
        const fileName = `${generateFilename(certificate, 0)}.${ext}`;
        try {
          const response = await fetch(fileUrl);
          const blob = await response.blob();
          downloadFile({ data: blob, fileName });
          return true;
        } catch (e) {
          console.error("Error downloading third-party certificate:", e);
          window.open(fileUrl, "_blank");
          return true;
        }
      }

      return !!(await generateTrainingCertificatePdf({
        trainingCertificates,
        subscriberInfo,
        download: true,
        debug,
      }));
    }
  } catch (e) {
    console.log(
      "certificateHelper.bulkDownloadTrainingCertificates.exception:",
      e.message
    );

    return false;
  }
};
